import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import {
  DraftItemsReqBodyType,
  DraftReportBodyType,
  IReportProduct,
  ISummaryOrder,
  ReportBodyType,
  ReportChangeBodyType,
  ReportChangePreQuantityBodyType,
  ReportFilterItemType,
  ReportSliceState,
  ReportStateBodyType,
  ReportStateResponse,
  ReportResponse,
  SummaryOrdersResponse,
  ReportRequestBodyType,
  StateEnum,
} from "./types";
import { revertAll } from "@/app/model/actions";

import { MONTH_NAMES_RU } from "@/shared/constants/otherConst";
import { ProductsFilterItemType } from "../product/type";

const initialState: ReportSliceState = {
  error: null,
  isLoading: false,
  isReportRefreshing: false,
  summaryOrderList: [],
  summaryOrderListForFilter: [],
  currentReportsState: null,
  reportProductList: null,
  filters: [
    {
      type: "",
      fields: {} as ReportFilterItemType,
    },
  ],
  totalAmount: 0,
  totalCount: 0,
  isFilterLoading: false,
  selectedOrderFilters: {},
  selectedDates: undefined,
  sortRules: {
    type: "desc",
    field: "orderNumber",
  },
  searchCode: null,
  orderSearchCode: null,
  totalItemsCount: 0,
  excludeItems: [
    "vendorId",
    "dealerOrderItems",
    "remainsQuantity",
    "productionPlanQuantity",
    "code",
    "modelName",
  ],
  reportHideColumnsList: [],
  selectedFilters: {},
  pageSize: 20,
  // pageSize: (+localStorage.getItem("psizeRep") as PageSizeType) || 20,
  currentOrder: null,
  loadingExcel: false,
  dataToFetch: [],
  dataToFetchPreQuantity: [],
  selectedProductId: null,
  sortedIds: [],

  isGenerateReport: false,
  errorReport: null,
  reportName: null,
};

// получение списка сводных заказов
export const fetchSummaryOrders = createAsyncThunk<
  SummaryOrdersResponse,
  void,
  { rejectValue: string }
>("reports/fetchSummaryOrders", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<SummaryOrdersResponse>("/report/order");
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

// получение сведений о готовности заказа
export const fetchSummaryOrderState = createAsyncThunk<
  Partial<StateEnum>,
  ReportRequestBodyType,
  { rejectValue: string }
>("reports/fetchSummaryOrderState", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<Partial<StateEnum>>(
      "/report/download/order/state",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//создание получение черновика
export const fetchDraftReport = createAsyncThunk<
  ReportResponse,
  DraftReportBodyType,
  { rejectValue: string }
>("reports/fetchDraftReport", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<ReportResponse>("/order/draft", body);
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//обновление кол-ва в нескольких черновиках
export const editDrafItemstReport = createAsyncThunk<
  DraftItemsReqBodyType,
  DraftItemsReqBodyType,
  { rejectValue: string }
>("reports/editDrafItemstReport", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.put<DraftItemsReqBodyType>(
      "/order/draft/items",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//обновление кол-ва в нескольких черновиках
export const editDraftPreQuantityItemstReport = createAsyncThunk<
  { items: ReportChangePreQuantityBodyType[] },
  { items: ReportChangePreQuantityBodyType[] },
  { rejectValue: string }
>(
  "reports/editDraftPreQuantityItemstReport",
  async (body, { rejectWithValue }) => {
    try {
      const response = await $api.put(
        "/order/draft/items/prequantity",
        JSON.stringify(body),
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

//обновление заказов
export const updateDealersOrder = createAsyncThunk<
  void,
  { year?: number; month?: number },
  { rejectValue: string }
>(
  "reports/updateDealersOrder",
  async ({ year, month }, { rejectWithValue }) => {
    try {
      await $api.post("order/draft/updateorders", { year, month });
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

/* Загрузка отчетов */
export const fetchReportState = createAsyncThunk<
  ReportStateResponse,
  ReportStateBodyType,
  { rejectValue: string }
>("reports/fetchReportState", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<ReportStateResponse>(
      "/report/order/state",
      body,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

/* Просмотр одного отчета */
export const fetchOneReport = createAsyncThunk<
  ReportResponse,
  ReportBodyType,
  { rejectValue: string }
>("reports/fetchOneReport", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<ReportResponse>(
      "report/order/month",
      body,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchReportFilters = createAsyncThunk<
  {
    filters: [{ type: "eq"; fields: ProductsFilterItemType }] | [];
    month: number;
    year: number;
  },
  {
    filters?: [{ type: "eq"; fields: ProductsFilterItemType }] | [];
    month?: number;
    year?: number;
  },
  { rejectValue: string }
>("report/fetchReportFilters", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post("/filters/report", JSON.stringify(body));
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchDraftReportFilters = createAsyncThunk<
  {
    filters: [{ type: "eq"; fields: ProductsFilterItemType }] | [];
    month: number;
    year: number;
  },
  {
    filters?: [{ type: "eq"; fields: ProductsFilterItemType }] | [];
    month?: number;
    year?: number;
  },
  { rejectValue: string }
>("report/fetchDraftReportFilters", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post("/filters/draft", JSON.stringify(body));
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setOrdersListMock() {},
    setSelectedReportsFilters(state, action) {
      state.searchCode = null;
      state.selectedFilters = action.payload;
    },
    setSelectedOrderReportsFilters(state, action) {
      state.searchCode = null;
      state.selectedOrderFilters = action.payload;
    },
    setSelectedDates(state, action) {
      state.searchCode = null;
      state.selectedDates = action.payload;
    },
    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.searchCode = action.payload;
    },
    setOrderSearchCode(
      state,
      action: PayloadAction<typeof state.orderSearchCode>,
    ) {
      state.selectedDates = [];
      state.orderSearchCode = action.payload;
    },
    setReportHideColumnsList(
      state,
      action: PayloadAction<(keyof IReportProduct)[]>,
    ) {
      state.reportHideColumnsList = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    findByCode(state, action: PayloadAction<string>) {
      if (action.payload) {
        state.summaryOrderList = state.summaryOrderListForFilter.filter(
          (order) => {
            const month = order.month > 11 ? order.month - 12 : order.month;
            const year = order.month > 11 ? order.year + 1 : order.year;
            const name =
              `Заказ на ${MONTH_NAMES_RU[month]} ${year}`.toLowerCase();
            if (name.includes(action.payload.toLowerCase())) {
              return true;
            } else {
              return false;
            }
          },
        );
      } else {
        state.summaryOrderList = state.summaryOrderListForFilter;
      }
    },
    findByDate(state, action) {
      const range = action.payload;
      if (range && range.length > 1 && range[0] && range[1]) {
        // picker (dayjs) отдаёт $M — месяц 0-based; order.month — 1-based (1-12).
        // Сравниваем абсолютным индексом (год * 12 + месяц), чтобы диапазон
        // корректно работал через границу года.
        const startIndex = range[0].$y * 12 + range[0].$M + 1;
        const endIndex = range[1].$y * 12 + range[1].$M + 1;
        state.summaryOrderList = state.summaryOrderListForFilter.filter(
          (order) => {
            const orderIndex = order.year * 12 + order.month;
            return orderIndex >= startIndex && orderIndex <= endIndex;
          },
        );
      } else {
        state.summaryOrderList = state.summaryOrderListForFilter;
      }
    },
    setCurrentOrder(state, action) {
      state.currentOrder = action.payload;
    },
    setLoadingExcel(state, action: PayloadAction<boolean>) {
      state.loadingExcel = action.payload;
    },

    setDataToFetch(state, action: PayloadAction<ReportChangeBodyType[]>) {
      state.dataToFetch = action.payload;
    },
    setDataToFetchPreQuantity(
      state,
      action: PayloadAction<ReportChangePreQuantityBodyType[]>,
    ) {
      state.dataToFetchPreQuantity = action.payload;
    },

    setSelectedProductId(state, action: PayloadAction<string>) {
      if (state.selectedProductId !== action.payload) {
        state.selectedProductId = action.payload;
      } else {
        state.selectedProductId = null;
      }
    },
    setSortedIds(state, action: PayloadAction<string[]>) {
      state.sortedIds = action.payload;
    },
    setReportName(state, action: PayloadAction<string>) {
      state.reportName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(fetchReportState.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchReportState.fulfilled, (state, action) => {
      state.currentReportsState = action.payload;
      state.error = null;
    });
    builder.addCase(fetchReportState.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(fetchOneReport.pending, (state) => {
      state.error = null;
      if (state.reportProductList?.length) {
        state.isReportRefreshing = true;
      } else {
        state.isLoading = true;
      }
    });
    builder.addCase(fetchOneReport.fulfilled, (state, action) => {
      state.reportProductList = action.payload.items;
      state.totalCount = action.payload.totalCount;
      state.isLoading = false;
      state.isReportRefreshing = false;
      state.error = null;
    });
    builder.addCase(fetchOneReport.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isReportRefreshing = false;
    });

    //получение черновика
    builder.addCase(fetchDraftReport.pending, (state) => {
      state.error = null;
      if (state.reportProductList?.length) {
        state.isReportRefreshing = true;
      } else {
        state.isLoading = true;
      }
    });
    builder.addCase(fetchDraftReport.fulfilled, (state, action) => {
      state.reportProductList = action.payload.items;
      state.totalCount = action.payload.totalCount;
      state.isLoading = false;
      state.isReportRefreshing = false;
      state.error = null;
    });
    builder.addCase(fetchDraftReport.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isReportRefreshing = false;
    });

    //изменение черновика
    builder.addCase(editDrafItemstReport.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(editDrafItemstReport.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
      state.dataToFetch = [];
    });
    builder.addCase(editDrafItemstReport.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    //изменение черновика
    builder.addCase(editDraftPreQuantityItemstReport.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(editDraftPreQuantityItemstReport.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
      state.dataToFetchPreQuantity = [];
    });
    builder.addCase(
      editDraftPreQuantityItemstReport.rejected,
      (state, action) => {
        state.error = action.payload;
        state.isLoading = false;
      },
    );

    //обновление заказов у дилеров
    builder.addCase(updateDealersOrder.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(updateDealersOrder.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(updateDealersOrder.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    builder.addCase(fetchSummaryOrders.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchSummaryOrders.fulfilled,
      (state, action: PayloadAction<{ items: ISummaryOrder[] }>) => {
        state.summaryOrderList = action.payload.items;
        state.summaryOrderListForFilter = action.payload.items;
        state.isLoading = false;
        state.error = null;
      },
    );

    builder.addCase(fetchSummaryOrders.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchReportFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(fetchReportFilters.fulfilled, (state, action) => {
      state.filters = action.payload.filters;
      state.isFilterLoading = false;
    });

    builder.addCase(fetchReportFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload;
    });
    builder.addCase(fetchDraftReportFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(fetchDraftReportFilters.fulfilled, (state, action) => {
      state.filters = action.payload.filters;
      state.isFilterLoading = false;
    });

    builder.addCase(fetchDraftReportFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload;
    });
    builder.addCase(fetchSummaryOrderState.pending, (state) => {
      state.isGenerateReport = true;
      state.error = null;
    });

    builder.addCase(fetchSummaryOrderState.fulfilled, (state, action) => {
      // state.reportName = action.payload;
      if (
        action.payload === StateEnum.Failed ||
        action.payload === StateEnum.Stopped ||
        action.payload === StateEnum.Cancelled
      )
        state.errorReport =
          "Генерация отчёта была прервана. Пробуем ещё раз...";
      if (action.payload === StateEnum.Completed) state.errorReport = null;
      if (
        action.payload !== StateEnum.Started &&
        action.payload !== StateEnum.Unknown
      )
        state.isGenerateReport = false;
    });

    builder.addCase(fetchSummaryOrderState.rejected, (state, action) => {
      state.isGenerateReport = false;
      state.errorReport = action.payload;
    });
  },
});

export const {
  setOrdersListMock,
  setSelectedReportsFilters,
  setSelectedOrderReportsFilters,
  setSelectedDates,
  setSortRules,
  setSearchCode,
  setOrderSearchCode,
  findByCode,
  setPageSize,
  findByDate,
  setReportHideColumnsList,
  setCurrentOrder,
  setLoadingExcel,
  setDataToFetch,
  setDataToFetchPreQuantity,
  setSelectedProductId,
  setSortedIds,
  setReportName,
} = reportsSlice.actions;
